import { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/utils/api";
import { toast } from "sonner";
import { ExternalLink, Loader2, Plus, RefreshCw, Search, Trash2 } from "lucide-react";

// Leads live in the WhatsApp CRM (crm.sendtoo.in); this page shows and edits the same records via /api/v2/crm-leads.
const CRM_URL = process.env.REACT_APP_CRM_URL || "https://crm.sendtoo.in";
const STATUS = {
  new: { label: "New", cls: "bg-sky-100 text-sky-700" },
  in_progress: { label: "In progress", cls: "bg-amber-100 text-amber-700" },
  won: { label: "Won", cls: "bg-emerald-100 text-emerald-700" },
  lost: { label: "Lost", cls: "bg-red-100 text-red-700" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-600" },
};
const TYPES = { quote: "Quote", pickup: "Pickup", enquiry: "Enquiry", other: "Other" };
const SERVICES = {
  domestic_courier: "Domestic courier", international_courier: "International courier", relocation: "Relocation",
  ftl_ptl: "FTL / PTL", air_cargo: "Air cargo", sea_cargo: "Sea cargo", warehousing: "Warehousing",
};
const TABS = [["open", "Open", "new,in_progress"], ["new", "New", "new"], ["in_progress", "In progress", "in_progress"],
  ["won", "Won", "won"], ["lost", "Lost", "lost"], ["cancelled", "Cancelled", "cancelled"], ["all", "All", ""]];
const sel = "h-9 w-full rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#159DFF]";
const inp = "h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#159DFF]";
const err = (e, fb) => e?.response?.data?.detail && typeof e.response.data.detail === "string" ? e.response.data.detail : fb;
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const pretty = (k) => k.replace(/_/g, " ").replace(/^\w/, (m) => m.toUpperCase());
const summary = (d) => Object.entries(d || {}).slice(0, 3).map(([k, v]) => `${pretty(k)}: ${v}`).join(" · ");
const Chip = ({ s }) => <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS[s].cls}`}>{STATUS[s].label}</span>;
const F = ({ label, children }) => <label className="block space-y-1"><span className="text-xs font-semibold text-slate-600">{label}</span>{children}</label>;

function LeadDialog({ open, lead, onClose, onSaved }) {
  const editing = !!lead;
  const [f, setF] = useState({});
  const [rows, setRows] = useState([]);
  const [opts, setOpts] = useState({ teams: [], users: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/v2/crm-leads/options").then((r) => setOpts(r.data)).catch(() => {});
    if (lead) {
      setF({ status: lead.status, lost_reason: lead.lost_reason || "", type: lead.type, service: lead.service || "", value: lead.value ?? "",
        notes: lead.notes || "", team_id: lead.team_id || "", assigned_user_id: lead.assigned_user_id || "" });
      setRows(Object.entries(lead.details || {}).map(([k, v]) => ({ k: pretty(k), v })));
    } else {
      setF({ phone: "", name: "", company: "", city: "", type: "quote", service: "", value: "", notes: "", team_id: "", assigned_user_id: "" });
      setRows([{ k: "Pickup pincode", v: "" }, { k: "Delivery location", v: "" }, { k: "Weight kg", v: "" }]);
    }
  }, [open, lead]);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const details = () => Object.fromEntries(rows.filter((r) => r.k.trim() && String(r.v).trim()).map((r) => [r.k.trim(), String(r.v).trim()]));

  const save = async (patch = {}) => {
    const b = { ...f, ...patch };
    const common = { type: b.type, service: b.service || null, details: details(), value: b.value === "" ? null : Number(b.value), notes: b.notes,
      team_id: b.team_id || null, assigned_user_id: b.assigned_user_id || null };
    setBusy(true);
    try {
      if (editing) {
        const r = await api.patch(`/v2/crm-leads/${lead.id}`, { ...common, status: b.status, lost_reason: b.lost_reason || null });
        toast.success(`${r.data.number} saved`); onSaved(r.data);
      } else {
        const r = await api.post("/v2/crm-leads", { ...common, phone: b.phone, name: b.name, company: b.company, city: b.city });
        toast.success(`Lead ${r.data.number} created`); onSaved(r.data); onClose();
      }
    } catch (e) { toast.error(err(e, "Could not save the lead")); } finally { setBusy(false); }
  };
  const setStatus = (s) => {
    if (s === "lost" && !(f.lost_reason || "").trim()) { set({ status: s }); toast.info("Add a reason for losing this lead, then Save"); return; }
    save({ status: s });
  };
  const users = opts.users.filter((u) => !f.team_id || (u.teams || []).includes(f.team_id));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[94vh] overflow-y-auto sm:max-w-2xl" data-testid="lead-dialog">
        <DialogHeader><DialogTitle>{editing ? `${lead.number} · ${lead.contact.name || lead.contact.phone}` : "New lead"}</DialogTitle></DialogHeader>
        {editing && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">{lead.contact.phone}{lead.contact.city ? ` · ${lead.contact.city}` : ""} · from {lead.source === "bot" ? "WhatsApp chatbot" : lead.source} · {when(lead.created_at)}</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS).map(([k, s]) => (
                <button key={k} type="button" disabled={busy} onClick={() => setStatus(k)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold ${f.status === k ? `${s.cls} ring-1 ring-current` : "border border-slate-200 text-slate-500 hover:text-slate-900"}`}>{s.label}</button>
              ))}
            </div>
            {f.status === "lost" && <input className={inp} value={f.lost_reason} onChange={(e) => set({ lost_reason: e.target.value })} placeholder="Why was it lost? (price, competitor, no reply…)" />}
            {lead.conversation_id && <a href={`${CRM_URL}/inbox/${lead.conversation_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#159DFF]"><ExternalLink className="h-4 w-4" /> Open WhatsApp chat in CRM</a>}
          </div>
        )}
        {!editing && (
          <div className="grid gap-3 sm:grid-cols-2">
            <F label="Mobile number"><input className={inp} value={f.phone || ""} onChange={(e) => set({ phone: e.target.value })} placeholder="98765 43210" data-testid="lead-phone" /></F>
            <F label="Name"><input className={inp} value={f.name || ""} onChange={(e) => set({ name: e.target.value })} /></F>
            <F label="Company (optional)"><input className={inp} value={f.company || ""} onChange={(e) => set({ company: e.target.value })} /></F>
            <F label="City (optional)"><input className={inp} value={f.city || ""} onChange={(e) => set({ city: e.target.value })} /></F>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-3">
          <F label="Type"><select className={sel} value={f.type || "quote"} onChange={(e) => set({ type: e.target.value })}>{Object.entries(TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></F>
          <F label="Service"><select className={sel} value={f.service || ""} onChange={(e) => set({ service: e.target.value })}><option value="">Not set</option>{Object.entries(SERVICES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></F>
          <F label="Value (₹, optional)"><input type="number" min="0" className={inp} value={f.value ?? ""} onChange={(e) => set({ value: e.target.value })} /></F>
          <F label="Team (CRM)"><select className={sel} value={f.team_id || ""} onChange={(e) => set({ team_id: e.target.value, assigned_user_id: "" })}><option value="">No team</option>{opts.teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></F>
          <F label="Assigned to (CRM user)"><select className={sel} value={f.assigned_user_id || ""} onChange={(e) => set({ assigned_user_id: e.target.value })}><option value="">Unassigned</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></F>
        </div>
        <F label="Details">
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <input className={inp} value={r.k} placeholder="e.g. Pickup pincode" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, k: e.target.value } : x)))} />
                <input className={inp} value={r.v} placeholder="Value" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, v: e.target.value } : x)))} />
                <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setRows([...rows, { k: "", v: "" }])} className="text-xs font-semibold text-[#159DFF]">+ Add detail</button>
          </div>
        </F>
        <F label="Notes"><textarea rows={3} className="w-full rounded-md border border-slate-300 p-3 text-sm" value={f.notes || ""} onChange={(e) => set({ notes: e.target.value })} placeholder="Follow-up notes, quoted price, next step…" /></F>
        <Button className="bg-[#159DFF] text-white hover:bg-[#0F84D9]" disabled={busy || (!editing && !String(f.phone || "").trim())} onClick={() => save()} data-testid="lead-save">
          {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{editing ? "Save changes" : "Create lead"}
        </Button>
        {editing && lead.history?.length > 0 && (
          <div className="border-t border-slate-200 pt-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">History</p>
            <ul className="space-y-1 text-xs text-slate-500">{[...lead.history].reverse().map((h, i) => <li key={i}>{when(h.at)} · <span className="font-semibold text-slate-700">{h.by}</span> · {h.action}</li>)}</ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Leads() {
  const [tab, setTab] = useState("open");
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [data, setData] = useState({ items: [], counts: {}, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState("");
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = TABS.find((t) => t[0] === tab)[2];
      const r = await api.get("/v2/crm-leads", { params: { status: status || undefined, q: q.trim() || undefined, type: type || undefined, page, page_size: 25 } });
      setData(r.data); setProblem("");
    } catch (e) { setProblem(err(e, "Could not load leads")); } finally { setLoading(false); }
  }, [tab, q, type, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [tab, q, type]);
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const c = data.counts || {};
  const count = (k) => (k === "open" ? (c.new || 0) + (c.in_progress || 0) : k === "all" ? Object.values(c).reduce((a, b) => a + b, 0) : c[k] || 0);

  return (
    <DashboardLayout title="Leads">
      <div className="mx-auto max-w-7xl p-4 sm:p-6" data-testid="leads-page">
        <div className="mb-1 flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">Leads</h1>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={load}><RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
            <Button size="sm" className="bg-[#159DFF] text-white hover:bg-[#0F84D9]" onClick={() => setAdding(true)} data-testid="lead-add"><Plus className="mr-1.5 h-4 w-4" />Add lead</Button>
          </div>
        </div>
        <p className="mb-4 text-sm text-slate-500">Quote and pickup requests from the WhatsApp chatbot, the CRM team and Sendtoo — the same list as in the CRM.</p>
        {problem && <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{problem}</div>}

        <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
          {TABS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${tab === k ? "bg-[#159DFF] text-white" : "border border-slate-200 bg-white text-slate-600"}`}>
              {label} <span className="opacity-70">{count(k)}</span>
            </button>
          ))}
        </div>
        <div className="mb-4 flex flex-wrap gap-2">
          <div className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input className={`${inp} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, lead number…" /></div>
          <div className="w-40"><select className={sel} value={type} onChange={(e) => setType(e.target.value)}><option value="">All types</option>{Object.entries(TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        </div>

        {!loading && data.items.length === 0 && !problem && <p className="rounded-xl border border-slate-200 bg-white p-8 text-sm text-slate-500">No leads here yet.</p>}
        <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white md:block">
          {data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Lead</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Request</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Owner</th><th className="px-4 py-3">Updated</th></tr>
              </thead>
              <tbody>
                {data.items.map((l) => (
                  <tr key={l.id} onClick={() => setOpen(l)} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{l.number}<div className="font-sans text-[11px] text-slate-400">{l.source === "bot" ? "chatbot" : l.source}</div></td>
                    <td className="px-4 py-3"><p className="font-semibold text-slate-900">{l.contact.name || "—"}</p><p className="font-mono text-xs text-slate-500">{l.contact.phone}</p></td>
                    <td className="px-4 py-3"><p className="text-slate-800">{TYPES[l.type]}{l.service ? ` · ${SERVICES[l.service]}` : ""}{l.value != null ? ` · ₹${l.value}` : ""}</p><p className="text-xs text-slate-500">{summary(l.details)}</p></td>
                    <td className="px-4 py-3"><Chip s={l.status} /></td>
                    <td className="px-4 py-3 text-xs text-slate-700">{l.assigned_user_name || "Unassigned"}<div className="text-slate-400">{l.team_name || ""}</div></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{when(l.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="space-y-2 md:hidden">
          {data.items.map((l) => (
            <button key={l.id} type="button" onClick={() => setOpen(l)} className="w-full rounded-xl border border-slate-200 bg-white p-4 text-left">
              <div className="flex items-start justify-between gap-2">
                <div><p className="font-semibold text-slate-900">{l.contact.name || l.contact.phone}</p><p className="font-mono text-xs text-slate-500">{l.number} · {l.contact.phone}</p></div>
                <Chip s={l.status} />
              </div>
              <p className="mt-2 text-sm text-slate-700">{TYPES[l.type]}{l.service ? ` · ${SERVICES[l.service]}` : ""}</p>
              <p className="text-xs text-slate-500">{summary(l.details)}</p>
            </button>
          ))}
        </div>
        {data.pages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3 text-sm">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <span className="text-slate-500">Page {page} of {data.pages}</span>
            <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        )}
        <LeadDialog open={!!open} lead={open} onClose={() => setOpen(null)} onSaved={(d) => { setOpen(d); load(); }} />
        <LeadDialog open={adding} lead={null} onClose={() => setAdding(false)} onSaved={() => load()} />
      </div>
    </DashboardLayout>
  );
}
